import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import SingleCompatibilityCard from "components/Actions/dialogs/components/SingleCompatibilityCard";
import { UNKNOWN } from "shared/constants/strings";
import { getVehicle } from "shared/functions";
import { TRI_SOURCES } from "shared/types/enums";

import { renderCanAdapterType } from "../../functions";
import { ORDER } from "../../SecondSelectContent/InternalLog/types/enums";
import { CUSTOM_SECOND_SELECT } from "../../SecondSelectContent/types/enums";
import { SCHEDULE_TASK_TYPE } from "../../types/enums";
import { CardsProps } from "./types";

const Cards = ({
    compatiblePlatforms,
    compatibleCount,
    totalCount,
    taskType,
    activeSource,
    secondSelectName,
    differentData,
    isCancelTasksChecked,
    schedule
}: CardsProps) => {
    const { t } = useTranslation();

    const {
        bluetoothPasswordProps,
        canAdapterProps,
        canVehiclesProps,
        featureControlProps,
        generalDropdownProps,
        internalLogProps
    } = differentData;

    const areAllCompatible = compatibleCount === totalCount;

    const getActiveSourceTitle = () => {
        if (activeSource === TRI_SOURCES.Selected) {
            return t("Dialog##selected devices");
        }

        if (activeSource === TRI_SOURCES.Filtered) {
            return t("Dialog##filtered devices");
        }

        return t("Dialog##platform compatibility##devices from file");
    };

    const renderCompatiblePlatforms = () => {
        const filteredPlatforms = compatiblePlatforms.filter(
            platform => platform !== UNKNOWN
        );

        return filteredPlatforms.map((platform, i) => {
            const isFirst = i === 0;

            return (
                <span
                    key={platform}
                    css={css({
                        display: "block",
                        marginTop: isFirst ? 0 : "8px"
                    })}
                >
                    {t(`Dialog##platform compatibility##platform`, {
                        title: t(`General##${platform}`)
                    })}
                </span>
            );
        });
    };

    const renderDifferentScenariosData = () => {
        switch (secondSelectName) {
            case undefined: {
                return null;
            }
            case CUSTOM_SECOND_SELECT.Logs: {
                const {
                    additionalData: { inputValue, order }
                } = internalLogProps;

                const filesToDownload =
                    order === ORDER.Specific
                        ? inputValue?.rawValue
                        : inputValue?.count;

                return (
                    <>
                        <SingleCompatibilityCard
                            title={t("Dialog##begin download")}
                            value={t(`Dialog##${order}`)}
                        />

                        <SingleCompatibilityCard
                            title={t("Dialog##files to download")}
                            value={filesToDownload}
                        />
                    </>
                );
            }
            case CUSTOM_SECOND_SELECT.CanAdapters: {
                const {
                    additionalData: {
                        adapter,
                        dateValue: { stringifiedDate },
                        revisionValue
                    }
                } = canAdapterProps;

                return (
                    <>
                        {adapter && (
                            <SingleCompatibilityCard
                                title={t("Dialog##can adapter")}
                                value={renderCanAdapterType(adapter)}
                            />
                        )}

                        <SingleCompatibilityCard
                            title={t("Dialog##firmware date")}
                            value={stringifiedDate || "-"}
                        />

                        {revisionValue !== null && (
                            <SingleCompatibilityCard
                                title={t("Dialog##revision")}
                                value={revisionValue || "-"}
                            />
                        )}
                    </>
                );
            }
            case CUSTOM_SECOND_SELECT.CanVehicles: {
                const {
                    additionalData: { make, model }
                } = canVehiclesProps;

                return (
                    <>
                        <SingleCompatibilityCard
                            title={t("Dialog##make")}
                            value={make}
                        />

                        <SingleCompatibilityCard
                            title={t("Dialog##vehicle model")}
                            value={getVehicle({
                                model: model!.model,
                                startYear: model!.year_start,
                                endYear: model!.year_end
                            })}
                        />
                    </>
                );
            }
            case CUSTOM_SECOND_SELECT.FeatureControl: {
                const { feature } = featureControlProps;

                return (
                    <SingleCompatibilityCard
                        title={t("Dialog##feature")}
                        value={t(`Dialog##${feature!.name}`)}
                    />
                );
            }
            case CUSTOM_SECOND_SELECT.SetBluetoothPassword: {
                const { passwordSource } = bluetoothPasswordProps;

                return (
                    <SingleCompatibilityCard
                        title={t(
                            "Dialog##set bluetooth password##password source"
                        )}
                        value={t(`Attributes##${passwordSource}`)}
                    />
                );
            }
            default: {
                const {
                    additionalData: { value }
                } = generalDropdownProps;

                return (
                    <SingleCompatibilityCard
                        title={t(`General##${secondSelectName}`)}
                        value={value!.name}
                    />
                );
            }
        }
    };

    const renderScheduleTime = () => {
        if (schedule) {
            const { type, fromDate, toDate } = schedule;

            const isByDateTaskType = type === SCHEDULE_TASK_TYPE.ByDate;

            return `${fromDate} ${isByDateTaskType ? t("Dialog##date to") : "-"} ${toDate}`;
        }

        return "-";
    };

    return (
        <div
            css={css({
                "& > div:last-of-type": areAllCompatible && {
                    borderBottom: "none"
                }
            })}
        >
            <SingleCompatibilityCard
                title={t(
                    "Dialog##platform compatibility##compatible model devices"
                )}
                value={compatibleCount}
            />

            <SingleCompatibilityCard
                title={getActiveSourceTitle()}
                value={totalCount}
            />

            <SingleCompatibilityCard
                title={t("General##task type")}
                value={t(`General##${taskType}`)}
            />

            {renderDifferentScenariosData()}

            <SingleCompatibilityCard
                title={t("Dialog##platform compatibility##task applies")}
                value={renderCompatiblePlatforms()}
            />

            <SingleCompatibilityCard
                title={t("Dialog##cancel pending tasks")}
                value={t(`General##${isCancelTasksChecked ? "yes" : "no"}`)}
            />

            <SingleCompatibilityCard
                title={t("Table##schedule")}
                value={renderScheduleTime()}
            />
        </div>
    );
};

export default Cards;
